import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { I18nProvider, useLingui } from '@lingui/react'
import { LuneThemeNew, LuneThemeProvider } from '@lune-fe/lune-ui-lib'
import DashboardApp from 'app/DashboardApp'
import { isAxiosError } from 'axios'
import { i18n, initI18n } from 'i18n'
import mixpanel from 'mixpanel-browser'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'

import { configCatClient } from 'config/ConfigCat'

import '@fontsource/space-mono'

import SnackbarProvider from './components/SnackbarProvider'
import { getUserData } from './endpoints/dapi'

import '@fontsource/space-mono/400.css'
import './index.css'

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 2,
            staleTime: 1000 * 60 * 15, // 15 mins
            cacheTime: 1000 * 60 * 15, // 15 mins
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    },
})

const AppRoot = () => {
    useLingui()

    return (
        <QueryClientProvider client={queryClient}>
            <LuneThemeProvider theme={LuneThemeNew}>
                <SnackbarProvider>
                    <DashboardApp />
                </SnackbarProvider>
            </LuneThemeProvider>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    )
}

const app = (
    <I18nProvider i18n={i18n}>
        <AppRoot />
    </I18nProvider>
)

if (process.env.REACT_APP_MIXPANEL_TOKEN && process.env.REACT_APP_MIXPANEL_PROXY) {
    // https://developer.mixpanel.com/docs/collection-via-a-proxy
    mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
        api_host: process.env.REACT_APP_MIXPANEL_PROXY,
    })
} else if (process.env.REACT_APP_MIXPANEL_TOKEN) {
    mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN)
}
// We could (and did) attempt to initialize but disable Mixpanel like so:
//
//     mixpanel.init('<dummy>', { test: true })
//     mixpanel.disable()
//
// but that didn't actually disable all Mixpanel tracking and was misleading.

const root = ReactDOM.createRoot(document.getElementById('root')!)
initI18n().finally(() => {
    root.render(app)
})

type ApiErrorResponse = { error?: { error_code: string; message: string } } | null

if (process.env.REACT_APP_BUGSNAG_KEY) {
    Bugsnag.start({
        apiKey: process.env.REACT_APP_BUGSNAG_KEY,
        plugins: [new BugsnagPluginReact()],
        onError: async (event) => {
            // Axios errors only carry the status code, so add the reason the API returned
            const apiError = isAxiosError<ApiErrorResponse>(event.originalError)
                ? event.originalError.response?.data?.error
                : undefined
            if (apiError) {
                event.errors[0].errorMessage += ` (${apiError.error_code}): ${apiError.message}`
            }
            try {
                const data = await getUserData()
                event.setUser(
                    data.user.id,
                    data.user.email,
                    `${data.user.firstname} ${data.user.lastname}`,
                )
                event.addMetadata(`account`, data.account)
            } catch {
                console.warn(`Unable to get metadata to pass to bugsnag`)
            }
        },
    })
}

if (configCatClient) {
    configCatClient
        .forceRefreshAsync()
        .then(() =>
            console.log(`Latest feature flag and configuration values successfully downloaded.`),
        )
}
